import {
    FC,
    KeyboardEvent,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import {
    CommandPalette,
    CommandPaletteProps,
} from "@design-system/CommandPalette/CommandPalette";
import { useFirefox } from "@hooks/useFirefox";
import { defaultFilter } from "@vendor/cmdk/cmdk";
import {
    HOTKEYS_SCOPES,
    useKeyboardCommand,
} from "@hooks/keyboard/useKeyboardCommand";
import { CommandsContextProvider } from "./commands.context";
import styles from "./Commands.module.scss";
import { AllCommandsList } from "./AllCommandsList";
import { AccountsCommandsList } from "./AccountsCommandsList";
import { CommandsDirectoryBreadcrumbs } from "./CommandsDirectoryBreadcrumbs";
import { OnboardingCommandsList } from "./OnboardingCommandsList";
import { StaticAccountsCommandsList } from "./StaticAccountsCommandsList";
import { WorkspacesCommandsList } from "./WorkspacesCommandsList";
import { CommandsDirectory, CommandsOptions } from "./lib";
import { TaskBulkCommandsList } from "./TaskBulkCommandsList";
import { TaskBulkStatusCommandsList } from "./TaskBulkStatusCommandsList";
import { TaskBulkMemberCommandsList } from "./TaskBulkMemberCommandsList";
import { TaskBulkPriorityCommandsList } from "./TaskBulkPriorityCommandsList";
import { TaskBulkTaskGroupCommandsList } from "./TaskBulkTaskGroupCommandsList";
import { TaskBulkEntityCommandsList } from "./TaskBulkEntityCommandsList";
import { DocumentBulkCommandsList } from "./DocumentBulkCommandsList";
import { DocumentBulkEntityCommandsList } from "./DocumentBulkEntityCommandsList";
import { DocumentBulkAccountCommandsList } from "./DocumentBulkAccountCommandsList";
import { DocumentBulkCategoryCommandsList } from "./DocumentBulkCategoryCommandsList";
import { TransactionBulkCommandsList } from "./TransactionBulkCommandsList";
import { TransactionBulkEntityCommandsList } from "./TransactionBulkEntityCommandsList";
import { TransactionBulkCounterpartyCommandsList } from "./TransactionBulkCounterpartyCommandsList";
import { TransactionBulkCustomerCommandsList } from "./TransactionBulkCustomerCommandsList";
import { TransactionBulkCategoryCommandsList } from "./TransactionBulkCategoryCommandsList";
import { TransactionBulkCoaCommandsList } from "./TransactionBulkCoaCommandsList";
import { TransactionBulkAccrualCoaCommandsList } from "./TransactionBulkAccrualCoaCommandsList";
import { TransactionBulkScheduleCommandsList } from "./TransactionBulkScheduleCommandsList";
import { TransactionBulkClassCommandsList } from "./TransactionBulkClassCommandsList";
import { TransactionBulkReviewFieldCommandsList } from "./TransactionBulkReviewFieldCommandsList";
import { CloseTemplateTaskBulkCommandsList } from "./CloseTemplateTaskBulkCommandsList";
import { CloseTemplateTaskBulkDeadlineCommandsList } from "./CloseTemplateTaskBulkDeadlineCommandsList";
import { CloseTemplateTaskBulkRoleCommandsList } from "./CloseTemplateTaskBulkRoleCommandsList";
import { CloseTemplateTaskBulkStageCommandsList } from "./CloseTemplateTaskBulkStageCommandsList";
import { CloseTemplateTaskBulkEstimateCommandsList } from "./CloseTemplateTaskBulkEstimateCommandsList";
import { ApplyTemplateEntityBulkCommandsList } from "./ApplyTemplateEntityBulkCommandsList";
import { ApplyTemplateEntityBulkMemberCommandsList } from "./ApplyTemplateEntityBulkMemberCommandsList";
import { ClientBulkCommandsList } from "./ClientBulkCommandsList";
import {
    ClientsWorkBulkCommandsList,
    ClientsWorkDateCommandsList,
    ClientsWorkMemberCommandsList,
    ClientsWorkTeamCommandsList,
} from "./ClientsWorkBulkCommandsList";
import { LineItemsBulkCommandsList } from "./LineItemsBulkCommandsList";
import { LineItemsBulkAccrualCoaCommandsList } from "./LineItemsBulkAccrualCoaCommandsList";
import { LineItemsBulkReceivablesCommandsList } from "./LineItemsBulkReceivablesCommandsList";
import { LineItemsBulkPayablesCommandsList } from "./LineItemsBulkPayablesCommandsList";
import { CatalogItemBulkCommandsList } from "./CatalogItemBulkCommandsList";
import { CatalogItemBulkAccountCommandsList } from "./CatalogItemBulkAccountCommandsList";
import { CatalogItemBulkClassCommandsList } from "./CatalogItemBulkClassCommandsList";
import { LineItemsBulkClassCommandsList } from "./LineItemsBulkClassCommandsList";
import { AccrualDraftBulkCommandsList } from "./AccrualDraftBulkCommandsList";
import { AccrualReviewBulkCommandsList } from "./AccrualReviewBulkCommandsList";
import { BillBulkCommandsList } from "./BillBulkCommandsList";
import { CounterpartyBulkCommandsList } from "./CounterpartyBulkCommandsList";
import { OccurrenceBulkCommandsList } from "./OccurrenceBulkCommandsList";
import { OccurrenceBulkMemberCommandsList } from "./OccurrenceBulkMemberCommandsList";
import { ScheduleBulkCommandsList } from "./ScheduleBulkCommandsList";
import { ScheduleApplicationBulkCommandsList } from "./ScheduleApplicationBulkCommandsList";
import { ScheduleApplicationBulkChangePolicyCommandsList } from "./ScheduleApplicationBulkChangePolicyCommandsList";
import { CloseTemplateBulkCommandsList } from "./CloseTemplateBulkCommandsList";
import { OrgIntegrationBulkCommandsList } from "./OrgIntegrationBulkCommandsList";
import { InvoiceTemplateBulkCommandsList } from "./InvoiceTemplateBulkCommandsList";
import { OrganizationTaskTemplateBulkCommandsList } from "./OrganizationTaskTemplateBulkCommandsList";
import { InvoiceBulkCommandsList } from "./InvoiceBulkCommandsList";
import { InvoiceBulkCounterpartyCommandsList } from "./InvoiceBulkCounterpartyCommandsList";
import { InvoiceBulkScheduleCommandsList } from "./InvoiceBulkScheduleCommandsList";
import { DocumentRetrievalBulkCommandsList } from "./DocumentRetrievalBulkCommandsList";
import { GetDocumentsBulkCommandsList } from "./GetDocumentsBulkCommandsList";
import { AgentRunCloseCommandsList } from "./AgentRunCloseCommandsList";

interface CommandsProviderContextProps {
    children: ReactNode;
    useDefaultKeyboardCommand?: boolean;
}
export const CommandsProvider: FC<CommandsProviderContextProps> = ({
    children,
    useDefaultKeyboardCommand = true,
}) => {
    const isFirefox = useFirefox();
    const [directories, setDirectories] = useState<CommandsDirectory[]>([]);
    const [commandOptions, setCommandOptions] = useState<CommandsOptions>({});
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const currentDirectory = directories[directories.length - 1];
    const commandRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const openOverrideRef = useRef<(() => void) | null>(null);

    const registerOpenOverride = useCallback((fn: () => void) => {
        const override = () => fn();
        openOverrideRef.current = override;
        return () => {
            if (openOverrideRef.current === override) {
                openOverrideRef.current = null;
            }
        };
    }, []);

    const unregisterOpenOverride = useCallback(() => {
        openOverrideRef.current = null;
    }, []);
    const open = useCallback(
        (
            directory: CommandsDirectory = CommandsDirectory.HOME,
            options: CommandsOptions = {},
        ) => {
            setCommandOptions(options);
            setIsOpen(true);

            if (inputRef.current) {
                inputRef.current.focus();
            }

            if (isOpen && directory === currentDirectory) {
                return;
            }

            if (isOpen && directory) {
                setSearch("");
                setDirectories((prev) => [...prev, directory]);
            } else {
                setDirectories(directory ? [directory] : []);
            }
        },
        [isOpen, currentDirectory],
    );

    const filterPrioritisingShortcuts: CommandPaletteProps["filter"] =
        useCallback(
            (value: string, searchValue: string, keywords?: string[]) => {
                if (
                    keywords?.includes(`shortcut:${searchValue.toLowerCase()}`)
                ) {
                    return 1; // shortcuts on top
                }

                return defaultFilter?.(value, searchValue, keywords) ?? 0;
            },
            [],
        );

    const close = useCallback(() => {
        setDirectories([]);
        setSearch("");
        setIsOpen(false);
    }, []);

    const returnToRoot = useCallback(() => {
        if (directories.length <= 1) {
            close();
            return;
        }
        setDirectories((prev) => prev.slice(0, 1));
        setSearch("");
        inputRef.current?.focus();
    }, [directories.length, close]);

    useKeyboardCommand({
        keys: "mod+k",
        callback: () => {
            if (openOverrideRef.current) {
                openOverrideRef.current();
                return;
            }
            open();
        },
        options: {
            enabled: useDefaultKeyboardCommand,
            description: "Open commands portal",
            scopes: [HOTKEYS_SCOPES.GLOBAL],
            // Ctrl+K focuses the omnibox in Chrome/Edge on Windows/Linux
            preventDefault: true,
        },
    });

    const handleDirectoryBack = useCallback(
        (e: KeyboardEvent) => {
            const isInputFocused = document.activeElement === inputRef.current;
            // Backspace goes to previous page when search is empty
            if (e.key === "Backspace" && !search && isInputFocused) {
                e.preventDefault();
                setDirectories((prev) =>
                    prev.length > 1 ? prev.slice(0, -1) : prev,
                );
            }

            // Clear search on Enter so the next directory starts with a clean input
            if (e.key === "Enter") {
                setSearch("");
            }
        },
        [search],
    );

    const value = useMemo(
        () => ({
            open,
            close,
            returnToRoot,
            replaceOptions: setCommandOptions,
            isOpen,
            registerOpenOverride,
            unregisterOpenOverride,
        }),
        [
            open,
            close,
            returnToRoot,
            isOpen,
            registerOpenOverride,
            unregisterOpenOverride,
        ],
    );

    const visibleCommands = useMemo(() => {
        // eslint-disable-next-line sonarjs/max-switch-cases
        switch (currentDirectory) {
            case CommandsDirectory.ACCOUNTS:
                return <AccountsCommandsList options={commandOptions} />;
            case CommandsDirectory.ONBOARDING:
                return <OnboardingCommandsList />;
            case CommandsDirectory.WORKSPACES:
                return <WorkspacesCommandsList groupHeading="Workspaces" />;
            case CommandsDirectory.TASK_BULK:
                return <TaskBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.TASK_BULK_STATUS:
                return <TaskBulkStatusCommandsList options={commandOptions} />;
            case CommandsDirectory.TASK_BULK_MEMBER:
                return <TaskBulkMemberCommandsList options={commandOptions} />;
            case CommandsDirectory.TASK_BULK_PRIORITY:
                return (
                    <TaskBulkPriorityCommandsList options={commandOptions} />
                );
            case CommandsDirectory.TASK_BULK_TASK_GROUP:
                return (
                    <TaskBulkTaskGroupCommandsList options={commandOptions} />
                );
            case CommandsDirectory.TASK_BULK_ENTITY:
                return <TaskBulkEntityCommandsList options={commandOptions} />;
            case CommandsDirectory.DOCUMENT_BULK:
                return <DocumentBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.DOCUMENT_BULK_ENTITY:
                return (
                    <DocumentBulkEntityCommandsList options={commandOptions} />
                );
            case CommandsDirectory.DOCUMENT_BULK_ACCOUNT:
                return (
                    <DocumentBulkAccountCommandsList options={commandOptions} />
                );
            case CommandsDirectory.DOCUMENT_BULK_CATEGORY:
                return (
                    <DocumentBulkCategoryCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK:
                return <TransactionBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.TRANSACTION_BULK_ENTITY:
                return (
                    <TransactionBulkEntityCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_COUNTERPARTY:
                return (
                    <TransactionBulkCounterpartyCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_CUSTOMER:
                return (
                    <TransactionBulkCustomerCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_CATEGORY:
                return (
                    <TransactionBulkCategoryCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_COA:
                return (
                    <TransactionBulkCoaCommandsList options={commandOptions} />
                );
            case CommandsDirectory.TRANSACTION_BULK_ACCRUAL_COA:
                return (
                    <TransactionBulkAccrualCoaCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_SCHEDULE:
                return (
                    <TransactionBulkScheduleCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_CLASS:
                return (
                    <TransactionBulkClassCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_MARK_REVIEWED:
                return (
                    <TransactionBulkReviewFieldCommandsList
                        options={commandOptions}
                        mode="markReviewed"
                    />
                );
            case CommandsDirectory.TRANSACTION_BULK_UNREVIEW:
                return (
                    <TransactionBulkReviewFieldCommandsList
                        options={commandOptions}
                        mode="unreview"
                    />
                );
            case CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK:
                return (
                    <CloseTemplateTaskBulkCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_DEADLINE:
                return (
                    <CloseTemplateTaskBulkDeadlineCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_ROLE:
                return (
                    <CloseTemplateTaskBulkRoleCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_STAGE:
                return (
                    <CloseTemplateTaskBulkStageCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_ESTIMATE:
                return (
                    <CloseTemplateTaskBulkEstimateCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK:
                return (
                    <ApplyTemplateEntityBulkCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_PREPARER:
                return (
                    <ApplyTemplateEntityBulkMemberCommandsList
                        options={commandOptions}
                        role="preparer"
                        optionsKey="applyTemplateEntityBulkPreparerOptions"
                    />
                );
            case CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_REVIEWER:
                return (
                    <ApplyTemplateEntityBulkMemberCommandsList
                        options={commandOptions}
                        role="reviewer"
                        optionsKey="applyTemplateEntityBulkReviewerOptions"
                    />
                );
            case CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_MANAGER:
                return (
                    <ApplyTemplateEntityBulkMemberCommandsList
                        options={commandOptions}
                        role="manager"
                        optionsKey="applyTemplateEntityBulkManagerOptions"
                    />
                );
            case CommandsDirectory.CLIENT_BULK:
                return <ClientBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.CLIENTS_WORK_BULK:
                return <ClientsWorkBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.CLIENTS_WORK_BULK_TEAM:
                return <ClientsWorkTeamCommandsList options={commandOptions} />;
            case CommandsDirectory.CLIENTS_WORK_BULK_PREPARER:
                return (
                    <ClientsWorkMemberCommandsList
                        options={commandOptions}
                        field="preparers"
                    />
                );
            case CommandsDirectory.CLIENTS_WORK_BULK_REVIEWER:
                return (
                    <ClientsWorkMemberCommandsList
                        options={commandOptions}
                        field="reviewers"
                    />
                );
            case CommandsDirectory.CLIENTS_WORK_BULK_MANAGER:
                return (
                    <ClientsWorkMemberCommandsList
                        options={commandOptions}
                        field="managers"
                    />
                );
            case CommandsDirectory.CLIENTS_WORK_BULK_WORK_START:
                return (
                    <ClientsWorkDateCommandsList
                        options={commandOptions}
                        field="workStartDate"
                    />
                );
            case CommandsDirectory.CLIENTS_WORK_BULK_WORK_END:
                return (
                    <ClientsWorkDateCommandsList
                        options={commandOptions}
                        field="workDeliveryDate"
                    />
                );
            case CommandsDirectory.LINE_ITEMS_BULK:
                return <LineItemsBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.LINE_ITEMS_BULK_ACCRUAL_COA:
                return (
                    <LineItemsBulkAccrualCoaCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.LINE_ITEMS_BULK_RECEIVABLES:
                return (
                    <LineItemsBulkReceivablesCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.LINE_ITEMS_BULK_PAYABLES:
                return (
                    <LineItemsBulkPayablesCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.LINE_ITEMS_BULK_CLASS:
                return (
                    <LineItemsBulkClassCommandsList options={commandOptions} />
                );
            case CommandsDirectory.CATALOG_ITEM_BULK:
                return <CatalogItemBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.CATALOG_ITEM_BULK_CLASS:
                return (
                    <CatalogItemBulkClassCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.CATALOG_ITEM_BULK_INCOME_ACCOUNT:
                return (
                    <CatalogItemBulkAccountCommandsList
                        options={commandOptions}
                        accountLabel="income account"
                        optionsKey="catalogItemBulkIncomeAccountOptions"
                    />
                );
            case CommandsDirectory.CATALOG_ITEM_BULK_RECEIVABLES_ACCOUNT:
                return (
                    <CatalogItemBulkAccountCommandsList
                        options={commandOptions}
                        accountLabel="receivables account"
                        optionsKey="catalogItemBulkReceivablesAccountOptions"
                    />
                );
            case CommandsDirectory.ACCRUAL_DRAFT_BULK:
                return (
                    <AccrualDraftBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.ACCRUAL_REVIEW_BULK:
                return (
                    <AccrualReviewBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.BILL_BULK:
                return <BillBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.COUNTERPARTY_BULK:
                return (
                    <CounterpartyBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.OCCURRENCE_BULK:
                return <OccurrenceBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.OCCURRENCE_BULK_PREPARER:
                return (
                    <OccurrenceBulkMemberCommandsList
                        options={commandOptions}
                        role="preparer"
                        optionsKey="occurrenceBulkPreparerOptions"
                    />
                );
            case CommandsDirectory.OCCURRENCE_BULK_REVIEWER:
                return (
                    <OccurrenceBulkMemberCommandsList
                        options={commandOptions}
                        role="reviewer"
                        optionsKey="occurrenceBulkReviewerOptions"
                    />
                );
            case CommandsDirectory.OCCURRENCE_BULK_MANAGER:
                return (
                    <OccurrenceBulkMemberCommandsList
                        options={commandOptions}
                        role="manager"
                        optionsKey="occurrenceBulkManagerOptions"
                    />
                );
            case CommandsDirectory.OCCURRENCE_BULK_CLIENT:
                return (
                    <OccurrenceBulkMemberCommandsList
                        options={commandOptions}
                        role="client"
                        optionsKey="occurrenceBulkClientOptions"
                    />
                );
            case CommandsDirectory.SCHEDULE_BULK:
                return <ScheduleBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.SCHEDULE_APPLICATION_BULK:
                return (
                    <ScheduleApplicationBulkCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.SCHEDULE_APPLICATION_BULK_CHANGE_POLICY:
                return (
                    <ScheduleApplicationBulkChangePolicyCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.CLOSE_TEMPLATE_BULK:
                return (
                    <CloseTemplateBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.ORG_INTEGRATION_BULK:
                return (
                    <OrgIntegrationBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.INVOICE_TEMPLATE_BULK:
                return (
                    <InvoiceTemplateBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.ORGANIZATION_TASK_TEMPLATE_BULK:
                return (
                    <OrganizationTaskTemplateBulkCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.INVOICE_BULK:
                return <InvoiceBulkCommandsList options={commandOptions} />;
            case CommandsDirectory.INVOICE_BULK_COUNTERPARTY:
                return (
                    <InvoiceBulkCounterpartyCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.INVOICE_BULK_SCHEDULE:
                return (
                    <InvoiceBulkScheduleCommandsList options={commandOptions} />
                );
            case CommandsDirectory.DOCUMENT_RETRIEVAL_BULK:
                return (
                    <DocumentRetrievalBulkCommandsList
                        options={commandOptions}
                    />
                );
            case CommandsDirectory.GET_DOCUMENTS_BULK:
                return (
                    <GetDocumentsBulkCommandsList options={commandOptions} />
                );
            case CommandsDirectory.AGENT_RUN_CLOSE:
                return <AgentRunCloseCommandsList />;
            default:
                return <AllCommandsList />;
        }
    }, [commandOptions, currentDirectory]);

    const isRootDirectory = directories.length === 1;
    const isDateStep =
        currentDirectory === CommandsDirectory.CLIENTS_WORK_BULK_WORK_START ||
        currentDirectory === CommandsDirectory.CLIENTS_WORK_BULK_WORK_END;
    const isStaticFooterVisible =
        currentDirectory === CommandsDirectory.ONBOARDING ||
        currentDirectory === CommandsDirectory.ACCOUNTS;

    return (
        <CommandsContextProvider value={value}>
            {children}

            <CommandPalette
                show={isOpen}
                onHide={close}
                search={search}
                onSearchChange={setSearch}
                onKeyDown={handleDirectoryBack}
                filter={filterPrioritisingShortcuts}
                emptyLabel={isDateStep ? null : undefined}
                commandRef={commandRef}
                inputRef={inputRef}
                className={classNames(styles.commands, {
                    [styles.onboardingView]:
                        currentDirectory === CommandsDirectory.ONBOARDING,
                    [styles.accountsView]:
                        currentDirectory === CommandsDirectory.ACCOUNTS,
                })}
                headerClassName={classNames({ [styles.firefox]: isFirefox })}
                footerClassName={classNames({ [styles.firefox]: isFirefox })}
                breadcrumbs={
                    <CommandsDirectoryBreadcrumbs
                        directories={directories}
                        label={
                            isRootDirectory
                                ? commandOptions.scopeLabel
                                : undefined
                        }
                    />
                }
                footer={
                    isStaticFooterVisible ? (
                        <StaticAccountsCommandsList
                            options={commandOptions}
                            currentDirectory={currentDirectory}
                        />
                    ) : null
                }
            >
                {visibleCommands}
                {isRootDirectory && commandOptions.includeGlobalCommands && (
                    <AllCommandsList />
                )}
            </CommandPalette>
        </CommandsContextProvider>
    );
};
